import { Stack, Tabs } from "@mantine/core";
import { useSearchParams } from "react-router";
import { useMe } from "../api/MeContext";
import * as standardsApi from "../api/standards";
import { firmCanHaveTeam } from "../layout/nav";
import { SecondaryButton } from "../ui/buttons";
import { FormButtons } from "../ui/FormButtons";
import { MetaText } from "../ui/MetaText";
import { Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { PageLoader } from "../ui/PageLoader";
import { PANEL_SPACE } from "../ui/tokens";
import { ColorsStylesTab } from "./standards/ColorsStylesTab";
import { LanguageLabelsTab } from "./standards/LanguageLabelsTab";
import { draftProblem, isDirty, lastSavedLine } from "./standardsModel";
import { useFirmStandards } from "./useFirmStandards";

/** Brand Configuration (firm-model spec §6.2, §10.2; Figma 14227:2527 and 15185:541): the
 * firm's standards in two tabs, one draft and one save. Admins edit; members read. */
export function StandardsPage({ api = standardsApi }: { api?: typeof standardsApi }) {
  const { state: meState, reload: reloadMe } = useMe();
  // Home's "Edit" on a Not Set row opens the right tab (?tab=colors or ?tab=language).
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "language" ? "language" : "colors";
  const me = meState.status === "ready" ? meState.me : null;
  const canEdit = Boolean(me?.capabilities.includes("edit_standards"));
  const showAllowToggles = me ? firmCanHaveTeam(me) : false;
  // /v1/me carries the new firmVersion after a save.
  const { loaded, loadError, saveError, clearSaveError, saving, savedNote, patch, discard, reload, save } = useFirmStandards(api, reloadMe);

  if (loadError) return <Notice tone="danger">{loadError}</Notice>;
  if (!loaded) return <PageLoader />;

  const { record, baseline, draft } = loaded;
  const dirty = canEdit && isDirty(draft, baseline);
  const problem = draftProblem(draft);
  const props = { draft, patch, canEdit, showAllowToggles };

  return (
    <Stack gap={PANEL_SPACE.page}>
      <PageHeader title="Brand Configuration" aside={<MetaText>{lastSavedLine(record)}</MetaText>} />
      {canEdit ? null : <Notice tone="info">Your firm's admin sets these. SlideBrain writes and reviews every deck in your firm by them.</Notice>}

      <Tabs variant="outline" defaultValue={initialTab} keepMounted={false}>
        <Tabs.List pl={PANEL_SPACE.field}>
          <Tabs.Tab value="colors">Colors & Styles</Tabs.Tab>
          <Tabs.Tab value="language">Language & Labels</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="colors" pt={PANEL_SPACE.page}>
          <Stack gap={PANEL_SPACE.page}>
            <ColorsStylesTab {...props} />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="language" pt={PANEL_SPACE.page}>
          <Stack gap={PANEL_SPACE.page}>
            <LanguageLabelsTab {...props} />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {saveError ? (
        <Notice
          tone="danger"
          onClose={clearSaveError}
          action={
            saveError.stale ? (
              <SecondaryButton size="xs" onClick={reload}>
                Reload
              </SecondaryButton>
            ) : null
          }
        >
          {saveError.message}
        </Notice>
      ) : null}
      {canEdit && problem ? <Notice tone="warning">{problem}</Notice> : null}
      {savedNote ? <Notice tone="success">Saved. New chat turns in PowerPoint use these within a minute.</Notice> : null}
      {canEdit ? (
        <FormButtons
          submitLabel="Save"
          onSubmit={() => void save()}
          onCancel={discard}
          submitting={saving}
          submitDisabled={!dirty || problem !== null}
          cancelDisabled={!dirty || saving}
        />
      ) : null}
    </Stack>
  );
}
